/**
 * Attachment policy for chat, split out of the hooks so the ONE place a file
 * is judged is testable without React (the repo's convention — see
 * screenShare.ts / dataSaver.ts). Images keep their contract in image.ts;
 * this module owns what a NON-IMAGE file may be (LK-009) and what an
 * inbound attachment payload may claim.
 *
 * The allowlist is deliberately one MIME type deep: `application/pdf`, max
 * 10 MB, one attachment per message. Every added type is a new attack surface
 * into a children's chat — a decision to make on purpose, with the API side
 * (which presigns per content-type and validates the key namespace per type),
 * never by loosening a regex.
 */

import type { ChatAttachment, ChatFileAttachment, ChatImageAttachment } from './types'

/** The non-image allowlist. One entry (LK-009); the picker attribute derives from it. */
const FILE_MIME_ALLOWLIST = /^application\/pdf$/i

/** Max PDF size. The API enforces the same ceiling at presign; this is the UX half. */
export const FILE_MAX_BYTES = 10 * 1024 * 1024

/** The picker/drop contract for everything chat accepts: images + allowlisted files. */
export const ACCEPT_FILES_ATTRIBUTE = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.pdf'

export function isSupportedChatFile(file: File): boolean {
  return FILE_MIME_ALLOWLIST.test(file.type)
}

export type FileRefusal = 'fileTooLarge' | 'notFile'

/** Why this file cannot be sent, or null if it can. Cheap and synchronous. */
export function chatFileRefusal(file: File): FileRefusal | null {
  if (!isSupportedChatFile(file)) return 'notFile'
  if (file.size > FILE_MAX_BYTES) return 'fileTooLarge'
  return null
}

/** The size line on a file chip: whole units, no decimals until they matter. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 KB'
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb < 10 ? Math.round(kb * 10) / 10 : Math.round(kb)} KB`
  const mb = kb / 1024
  return `${mb < 10 ? Math.round(mb * 10) / 10 : Math.round(mb)} MB`
}

/** Display-only, like image.ts's sanitizeName: short, no path separators. */
export function sanitizeFilename(name: string): string {
  return (name.split(/[\\/]/).pop() || 'file.pdf').slice(0, 120)
}

const clampFileMetaNumber = (value: unknown, max: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(0, Math.round(value))) : 0

/**
 * Trust only what a file chip renders from a peer's attachment payload — the
 * server already validated the key belongs to the run; everything here is
 * display metadata an untamed peer could otherwise aim at the layout.
 * Image payloads keep their pre-union shape (no `kind`).
 */
export function sanitizeIncomingAttachment(raw: unknown): ChatAttachment | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const a = raw as Record<string, unknown>
  if (typeof a.key !== 'string' || !a.key) return undefined

  if (a.kind === 'file') {
    const file: ChatFileAttachment = {
      kind: 'file',
      key: a.key,
      filename:
        typeof a.filename === 'string' && a.filename ? sanitizeFilename(a.filename) : 'file.pdf',
      size: clampFileMetaNumber(a.size, FILE_MAX_BYTES),
      content_type:
        typeof a.content_type === 'string' && FILE_MIME_ALLOWLIST.test(a.content_type)
          ? a.content_type
          : 'application/pdf',
    }
    return file
  }

  // Clamp to sane pixels: a tampered peer sending Infinity/NaN/negative would
  // otherwise produce NaN or negative CSS box sizes.
  const clampDim = (v: unknown) =>
    typeof v === 'number' && Number.isFinite(v) ? Math.min(1600, Math.max(1, Math.round(v))) : 1
  const image: ChatImageAttachment = {
    key: a.key,
    thumb_key: typeof a.thumb_key === 'string' ? a.thumb_key : null,
    w: clampDim(a.w),
    h: clampDim(a.h),
    name: typeof a.name === 'string' ? a.name.slice(0, 120) : null,
  }
  return image
}
